import Image from 'next/image';
import { useAmazonGiftCardQuery, usePhrase } from '@/hooks';
import { Box, Button, Grid, Typography } from '@mui/material';
import { Currency } from '@/components/general';
import { WALLET_PHRASES } from '@/consts/phrases/walletPhrases';
import { CenteredLoadingSpinner } from '@/components/general/CenteredLoadingSpinner/CenteredLoadingSpinner';
import { ErrorAlert } from '@/components/forms';
import { getFetchErrors } from '@/utils/getFetchErrorMessage';
import { format } from 'date-fns/format';
import { config } from '@/config';

import AmazonGiftCardImage from '@/assets/theme/default/walletImages/amazon_gift_card.png';
import AmazonPayImage from '@/assets/theme/default/walletImages/amazon_pay_card.png';

import styles from './amazonGiftCard.module.css';

export const M3INDIA_AMAZONPAY_METHOD_ID = 20;
const AMAZON_PAY_METHODS = [M3INDIA_AMAZONPAY_METHOD_ID];

interface AmazonGiftCardProps {
    transactionId: number;
    transactionMethodId: number;
    currencyCode: string;
    currencySymbol: string | undefined;
    currencySymbolPosition: string | undefined;
}

export const AmazonGiftCard = (props: AmazonGiftCardProps) => {
    const { t } = usePhrase();
    const { amazonGiftCard, isLoading, error } = useAmazonGiftCardQuery(props.transactionId);

    const hasAmazonPayImage =
        props.transactionMethodId && AMAZON_PAY_METHODS.includes(props.transactionMethodId);

    const errorMessage = getFetchErrors(error);
    return (
        <>
            {!amazonGiftCard && error && (
                <ErrorAlert id="error_message" errorMessage={errorMessage} />
            )}
            {amazonGiftCard && !isLoading && (
                <>
                    <Grid container direction={['column', 'row']}>
                        <Grid size={{ xs: 12, sm: 3.8 }}>
                            <Box className={styles.infoBlock}>
                                <Image
                                    src={hasAmazonPayImage ? AmazonPayImage : AmazonGiftCardImage}
                                    className={styles.amazonGiftCardImage}
                                    alt={hasAmazonPayImage ? 'Amazon pay card' : 'Amazon gift card'}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3.8 }} sx={{ mt: [1, 0] }}>
                            <Box className={styles.infoBlock}>
                                <Typography variant="subtitle2" className={styles.subtitle}>
                                    {t(WALLET_PHRASES.GIFT_CARD_AMOUNT)}
                                </Typography>
                                <Typography variant="h4">
                                    <Currency
                                        code={props.currencyCode}
                                        position={props.currencySymbolPosition}
                                        symbol={props.currencySymbol}
                                        amount={amazonGiftCard?.amount}
                                    />
                                </Typography>
                            </Box>
                            {amazonGiftCard.giftCardExpiryDate && (
                                <Box className={styles.infoBlock}>
                                    <Typography variant="subtitle2" className={styles.subtitle}>
                                        {t(WALLET_PHRASES.VALID_UNTIL)}
                                    </Typography>
                                    <Typography variant="body1">
                                        {format(
                                            new Date(amazonGiftCard.giftCardExpiryDate),
                                            config.dateFormat,
                                        )}
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4.4 }} sx={{ mt: [1, 0] }}>
                            <Box className={styles.infoBlock}>
                                <Typography variant="subtitle2" className={styles.subtitle}>
                                    {t(WALLET_PHRASES.CLAIM_CODE)}
                                </Typography>
                                <Typography variant="h4">
                                    {amazonGiftCard.giftCardClaimCode}
                                </Typography>
                            </Box>
                            {amazonGiftCard.giftCardReferenceId && (
                                <Box className={styles.infoBlock}>
                                    <Typography variant="subtitle2" className={styles.subtitle}>
                                        {t(WALLET_PHRASES.REFERENCE_ID)}
                                    </Typography>
                                    <Typography variant="body1">
                                        {amazonGiftCard.giftCardReferenceId}
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                    <Grid container className={styles.redeemButtonRow}>
                        <a href={amazonGiftCard.redeemNowUrl} target="_blank" rel="noreferrer">
                            <Button variant="contained">{t(WALLET_PHRASES.REDEEM_NOW)}</Button>
                        </a>
                    </Grid>
                    <Grid container className={styles.redeemTextRow}>
                        <Typography variant="subtitle2">
                            <a
                                href={amazonGiftCard.redeemUrl}
                                className={styles.redeemLink}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {t(WALLET_PHRASES.REDEEM_AMAZON_CARD)}
                            </a>
                        </Typography>
                    </Grid>
                    <Grid container className={styles.restrictionsRow}>
                        <Typography variant="subtitle2">
                            <a
                                href={amazonGiftCard.legalUrl}
                                target="_blank"
                                rel="noreferrer"
                                className={styles.restrictionsLink}
                            >
                                {t(WALLET_PHRASES.CARD_RESTRICTIONS)}
                            </a>
                        </Typography>
                    </Grid>
                </>
            )}
            {isLoading && <CenteredLoadingSpinner />}
        </>
    );
};
